// Helpers puros usados pelo fluxo de criação em lote. Movidos para módulo próprio
// para desafogar LoteDialog.jsx (agora só orquestração + JSX).
import { calcTroco, sugerirTrocoPara } from "@/lib/api";
import { SEM_TROCO_FORMAS } from "@/pages/entregas/entregasHelpers";
// fev/2026: máscara e normalização do BR movidas pra `@/lib/phoneUtils`
// (fonte única, compartilhada entre form individual e lote).
export { formatTelBR, normalizePhoneBR } from "@/lib/phoneUtils";

export const emptyLoteRow = () => ({
  _uid: Math.random().toString(36).slice(2),
  bairro_id: "", regiao_id: "",
  // Iter 100: rota por linha — "" = automática pelo bairro (backend resolve); id = escolha manual (bairro em 2+ rotas).
  rota_id: "",
  // fev/2026: Tipo e Forma de Pagamento agora são POR LINHA.
  // O cabeçalho "Aplicar a todas as linhas" mantém apenas Data, Entregador e Status.
  tipo_id: "", forma_pgto_id: "",
  numero_pedido: "",
  numero_pedido_receita: "", numero_pedido_revenda: "",
  // Semântica nova (fev/2026): pedidos_multiplos guarda TODOS os N números quando
  // qtd_pedidos > 1. Cada input labelado "Nº do Pedido 1..N" na tabela edita este array.
  // numero_pedido é sincronizado com pedidos_multiplos[0] no submit pra manter
  // compatibilidade com tabela/relatórios/dashboard.
  qtd_pedidos: 1,
  pedidos_multiplos: [],
  cliente_nome: "", cliente_telefone: "",
  // Buscar Receita — mesma semântica do form individual (fev/2026)
  buscar_receita: false,
  // fev/2026: troco por linha (mesma lógica do formulário individual).
  // valor_recebido não é editado na tabela (mantido por compat com payload/backend).
  valor: "", valor_recebido: "", troco_para: "", troco: 0,
  observacao: "",
});

export const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// fev/2026 (v5): Janela de duplicidade — busca entregas cadastradas nos últimos
// 30 dias (contando a partir de `refDate` — geralmente a data da entrega sendo
// cadastrada). Backend `/entregas?start=X` aceita filtro por `data`.
// Retorna string YYYY-MM-DD.
export const DUP_WINDOW_DAYS = 30;
export const dupWindowStart = (refDate) => {
  const base = refDate ? new Date(`${refDate}T00:00:00`) : new Date();
  if (isNaN(base.getTime())) return todayStr();
  base.setDate(base.getDate() - DUP_WINDOW_DAYS);
  return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-${String(base.getDate()).padStart(2, "0")}`;
};

// Defaults do cabeçalho "Aplicar a todas as linhas".
// Status padrão = "aguardando" (linha entra sem entregador atribuído).
// Entregador padrão = "" (mostra "Selecione").
export const initialCommon = () => ({
  entregador_id: "",
  data: todayStr(), status: "aguardando",
});

export const LOTE_NONE_OPT = { value: "__none__", label: "Selecione" };

// Retorna apenas as linhas com algum campo preenchido (usado tanto para contagem quanto para submit).
export const filterValidRows = (rows) =>
  rows.filter((r) => {
    if (Number(r.valor) > 0) return true;
    if (r.numero_pedido || r.numero_pedido_receita || r.numero_pedido_revenda) return true;
    if (r.bairro_id || r.cliente_nome || r.cliente_telefone) return true;
    if (Array.isArray(r.pedidos_multiplos) && r.pedidos_multiplos.some(Boolean)) return true;
    return false;
  });

// Detecta se o tipo escolhido é "Receita+Revenda" (nome contém ambas as palavras).
// Determina se as linhas usam os dois campos de número (receita/revenda) ou apenas um.
export const computeIsReceitaRevenda = (tipoId, tipos) => {
  if (!tipoId) return false;
  const t = tipos.find((x) => x.id === tipoId);
  const nome = (t?.nome || "").toLowerCase();
  return nome.includes("receita") && nome.includes("revenda");
};

// Detecta se a forma de pagamento não devolve troco (pix, cartão, cheque, crediário, convênio, já pago).
// Reaproveita SEM_TROCO_FORMAS do formulário individual para manter regra idêntica.
export const computeIsSemTroco = (formaId, formas) => {
  if (!formaId) return false;
  const nome = (formas.find((x) => x.id === formaId)?.nome || "").toLowerCase();
  return SEM_TROCO_FORMAS.some((s) => nome.includes(s));
};

// Atualiza uma linha específica; propaga efeitos colaterais (troco, região, redimensionar pedidos_multiplos).
// Espelha a mesma lógica de `setField` do formulário individual em Entregas.jsx.
export const applyRowUpdate = (rows, i, field, val, bairros, formas = []) => {
  const nr = [...rows];
  nr[i] = { ...nr[i], [field]: val };
  if (field === "bairro_id") {
    const b = bairros.find((x) => x.id === val);
    if (b?.regiao_id) nr[i].regiao_id = b.regiao_id;
    nr[i].rota_id = "";
  }
  // Se muda qtd_pedidos, redimensiona pedidos_multiplos preservando o que já foi digitado.
  if (field === "qtd_pedidos") {
    const q = Math.max(1, Math.min(20, parseInt(val, 10) || 1));
    const atual = Array.isArray(nr[i].pedidos_multiplos) ? nr[i].pedidos_multiplos : [];
    if (q === 1) nr[i].pedidos_multiplos = [];
    else if (q > atual.length) {
      // Aproveita numero_pedido como pedido 1 se lista estava vazia
      const base = atual.length === 0 && nr[i].numero_pedido ? [nr[i].numero_pedido] : atual;
      nr[i].pedidos_multiplos = [...base, ...Array(q - base.length).fill("")];
    } else {
      nr[i].pedidos_multiplos = atual.slice(0, q);
    }
    // Sincroniza numero_pedido com o primeiro do array
    if (q > 1 && nr[i].pedidos_multiplos[0]) nr[i].numero_pedido = nr[i].pedidos_multiplos[0];
  }
  // Auto-sugere troco_para quando o valor muda (regra: R$ 50 até 50, R$ 100 acima, etc).
  if (field === "valor") {
    nr[i].troco_para = sugerirTrocoPara(val);
  }
  // Recalcula troco em tempo real. Se forma é "sem troco", zera tudo (mesma lógica do form individual).
  if (computeIsSemTroco(nr[i].forma_pgto_id, formas)) {
    nr[i].troco = 0;
    nr[i].troco_para = 0;
    nr[i].valor_recebido = 0;
  } else {
    nr[i].troco = calcTroco(nr[i].valor, nr[i].valor_recebido, nr[i].troco_para);
  }
  return nr;
};

// ---------------------------------------------------------------------------
// Duplicidade — nova em fev/2026 · Refinada fev/2026 (v4) por pedido do usuário
// ---------------------------------------------------------------------------
// Coleta TODOS os números de pedido não-vazios de uma linha do lote OU de uma
// entrega vinda do backend. Usada tanto pra detectar colisão entre linhas do
// próprio lote quanto contra entregas já persistidas no banco.
export const collectOrderNumbers = (obj) => {
  const nums = new Set();
  const add = (v) => {
    const s = String(v || "").trim();
    if (s) nums.add(s);
  };
  add(obj.numero_pedido);
  add(obj.numero_pedido_receita);
  add(obj.numero_pedido_revenda);
  if (Array.isArray(obj.pedidos_multiplos)) obj.pedidos_multiplos.forEach(add);
  return nums;
};

// Regra fev/2026 (v4) definida pelo usuário + refino (v4.2):
// - Duplicidade contra entregas com status em BLOCKING = TRAVA o salvamento
//   (aguardando, aguardando_conferencia, em_transito, concluida — entregas "reais" em andamento)
// - Duplicidade contra WARN_ONLY = APENAS alerta visual mas permite salvar
//   (remarcar, cancelada — entrega foi anulada, número pode ser reutilizado)
// - Duplicidade entre duas linhas do mesmo LOTE = sempre TRAVA
export const DUP_BLOCKING_STATUSES = new Set(["aguardando", "aguardando_conferencia", "em_transito", "concluida"]);
export const DUP_WARN_ONLY_STATUSES = new Set(["remarcar", "cancelada"]);

// Retorna Map<rowIndex, { numbers, matches, blocking }>
// - matches: array de { number, source: "lote"|"db", status, entrega_data }
// - blocking: true se pelo menos um match está em BLOCKING_STATUSES OU é entre linhas do lote
// - Todas as colisões entram no map (mesmo warn-only), pra o usuário ver o motivo.
//
// fev/2026 (v5): Considera TODAS as entregas em `items` — chamador é responsável
// por já ter feito fetch da janela (ex: últimos 30 dias). A regra BLOCK/WARN
// é aplicada por STATUS independente da data do match (mesma data ou passada).
// Data da entrega existente é anexada ao match pra UI exibir "cadastrada em DD/MM".
export const computeDuplicateInfo = (rows, items, dataStr) => {
  const info = new Map();
  if (!Array.isArray(rows) || rows.length === 0) return info;

  const rowNums = rows.map(collectOrderNumbers);

  // Contagem entre linhas do lote (>1 linha com o mesmo número)
  const loteCounts = new Map();
  rowNums.forEach((s) => s.forEach((n) => loteCounts.set(n, (loteCounts.get(n) || 0) + 1)));

  // Índice do DB: número → lista de {status, id, entrega_data}.
  // Considera TODAS as entregas de `items` (chamador fornece janela de 30d).
  const dbMap = new Map();
  (items || []).forEach((e) => {
    if (!e) return;
    collectOrderNumbers(e).forEach((n) => {
      if (!dbMap.has(n)) dbMap.set(n, []);
      dbMap.get(n).push({
        status: e.status,
        id: e.id,
        cliente: e.cliente_nome,
        entrega_data: e.data || (e.created_at || "").slice(0, 10),
      });
    });
  });

  rows.forEach((_, i) => {
    const numbers = [...rowNums[i]];
    const matches = [];
    let blocking = false;
    numbers.forEach((n) => {
      (dbMap.get(n) || []).forEach((h) => {
        matches.push({
          number: n,
          source: "db",
          status: h.status,
          cliente: h.cliente,
          entrega_id: h.id,
          entrega_data: h.entrega_data,
          same_date: h.entrega_data === dataStr,
        });
        if (DUP_BLOCKING_STATUSES.has(h.status)) blocking = true;
      });
      if ((loteCounts.get(n) || 0) > 1) {
        matches.push({ number: n, source: "lote", status: "aguardando" });
        blocking = true;
      }
    });
    if (matches.length > 0) info.set(i, { numbers, matches, blocking });
  });

  return info;
};

// Mantido apenas por compatibilidade externa — não usado internamente após v4.
export const DUP_IGNORED_STATUS = DUP_WARN_ONLY_STATUSES;

// Constrói payload de UMA entrega a partir do `common` (cabeçalho) + `row` (linha).
// Isola o branch Receita+Revenda para reduzir complexidade do submit no dialog.
// fev/2026: tipo_id e forma_pgto_id vêm da LINHA (não mais do cabeçalho).
export const buildEntregaPayload = (row, common, bairros, tipos) => {
  const bairroObj = bairros.find((b) => b.id === row.bairro_id);
  const rowIsReceitaRevenda = computeIsReceitaRevenda(row.tipo_id, tipos);
  const base = {
    entregador_id: common.entregador_id || null,
    tipo_id: row.tipo_id || null,
    forma_pgto_id: row.forma_pgto_id || null,
    bairro_id: row.bairro_id || null,
    rota_id: row.rota_id || null,
    regiao_id: row.regiao_id || bairroObj?.regiao_id || null,
    cliente_nome: row.cliente_nome || null,
    cliente_telefone: row.cliente_telefone || null,
    valor: Number(row.valor) || 0,
    valor_recebido: Number(row.valor_recebido) || 0,
    troco_para: Number(row.troco_para) || 0,
    troco: Number(row.troco) || 0,
    buscar_receita: !!row.buscar_receita,
    observacao: (row.observacao || "").trim() || null,
    status: common.status || "aguardando",
    data: common.data,
  };
  if (rowIsReceitaRevenda) {
    base.numero_pedido_receita = row.numero_pedido_receita || null;
    base.numero_pedido_revenda = row.numero_pedido_revenda || null;
    base.numero_pedido = null;
  } else {
    const qtd = Math.max(1, parseInt(row.qtd_pedidos, 10) || 1);
    if (qtd > 1) {
      // Múltiplos pedidos: envia lista completa + qtd_pedidos + espelha primeiro no numero_pedido
      const lista = (Array.isArray(row.pedidos_multiplos) ? row.pedidos_multiplos : [])
        .map((s) => String(s || "").trim())
        .filter(Boolean);
      base.qtd_pedidos = qtd;
      base.pedidos_multiplos = lista;
      base.numero_pedido = lista[0] || row.numero_pedido || null;
    } else {
      base.numero_pedido = row.numero_pedido || null;
    }
  }
  return base;
};
